import API, { ZitadelToken, ZitadelTokenCreateResponse } from "@/api/api";
import { toast } from "@/lib/instrumented-toast";
import { useOrgStore } from "@/stores";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const api = new API();

export function useZitadelTokens(orgId?: string) {
  return useQuery({
    queryKey: ["zitadel-tokens", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const tokens = await api.listZitadelTokens(orgId);
      return tokens;
    },
    enabled: !!orgId,
  });
}

export function useGenerateZitadelToken() {
  const queryClient = useQueryClient();
  const selectedOrgId = useOrgStore.use.selectedOrgId();

  return useMutation({
    mutationFn: async (orgId: string) => {
      return await api.generateZitadelToken(orgId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zitadel-tokens", selectedOrgId] });
      toast.success("Personal access token generated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to generate token: ${error.message}`);
    },
  });
}

export function useDeleteZitadelToken() {
  const queryClient = useQueryClient();
  const selectedOrgId = useOrgStore.use.selectedOrgId();

  return useMutation({
    mutationFn: async ({ orgId, tokenId }: { orgId: string; tokenId: string }) => {
      await api.deleteZitadelToken(orgId, tokenId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zitadel-tokens", selectedOrgId] });
      toast.success("Token deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete token: ${error.message}`);
    },
  });
}

export type { ZitadelToken, ZitadelTokenCreateResponse };
